import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Clapperboard, MapPin, Monitor, Film, ArrowRight, Sparkles, GitBranch } from "lucide-react";
import { getImportedShots, visualizeImport, proposeExpansion } from "@/services/breakdownService";
import { resolveWorld } from "@/constants/breakdownSettings";
import ShotBreakdownImportWizard from "@/components/breakdown/ShotBreakdownImportWizard";
import ExpansionReviewPanel from "@/components/breakdown/ExpansionReviewPanel";
import AutoStoryboardProgress from "@/components/AutoStoryboardProgress";

/** A single imported shot, read-only summary (settings chips + world + render kind).
 *  `child` styles it as a nested micro-insert; `onExpand` adds the Expand action. */
function ImportedShot({ anchor, child = false, onExpand, expanding }) {
  const s = anchor.planned_settings || {};
  const world = resolveWorld(anchor.world_tag);
  const isScreen = anchor.render_kind === "screen_capture";
  const rendered = (anchor.shots || []).length > 0;
  const expanded = anchor.status === "expanded";
  const chips = [s.shot_type, s.camera_movement !== "static" && s.camera_movement, s.lens, s.lighting].filter(Boolean);
  const canExpand = !child && !expanded && onExpand;

  return (
    <div className={`relative flex items-stretch gap-3 px-3 py-2 rounded-lg border ${
      child ? "border-[rgba(255,255,255,0.6)] bg-[var(--bg-elevated)] ml-6" : "border-[var(--border-subtle)] bg-[var(--bg-muted)]"
    }`}>
      <span className={`absolute left-0 top-1 bottom-1 w-1 rounded-full ${world.bar}`} aria-hidden />
      <div className="flex flex-col items-center justify-center w-12 flex-shrink-0 pl-1">
        <span className="text-xs font-semibold text-[var(--text-primary)]">{child ? "↳" : "#"}{anchor.authored_shot_number ?? "—"}</span>
        <span className="text-[9px] text-[var(--text-muted)]">{anchor.timecode}</span>
      </div>
      <div className="flex-1 min-w-0 self-center">
        <p className="text-[11px] text-[var(--text-secondary)] leading-snug line-clamp-2">{anchor.selected_text}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {chips.map((c, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--bg-elevated)] text-[var(--text-muted)] border border-[var(--border-subtle)]">{c}</span>
          ))}
        </div>
      </div>
      <div className="flex flex-col items-end justify-center gap-1 w-28 flex-shrink-0">
        <span className={`text-[9px] px-1.5 py-0.5 rounded border ${world.chip}`}>{world.label}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded border flex items-center gap-1 ${
          isScreen ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-[var(--bg-elevated)] text-[var(--text-muted)] border-[var(--border-subtle)]"
        }`}>
          {isScreen ? <Monitor size={10} /> : <Film size={10} />}{isScreen ? "Screen" : "Photoreal"}
        </span>
        <span className={`text-[9px] ${expanded ? "text-[var(--color-gold-400)]" : rendered ? "text-emerald-400" : "text-[var(--text-muted)]"}`}>
          {expanded ? "expanded" : rendered ? "rendered" : "pending"}
        </span>
        {canExpand && (
          <button
            onClick={() => onExpand(anchor)}
            disabled={expanding}
            className="text-[9px] px-1.5 py-0.5 rounded border border-[rgba(212,168,83,0.3)] text-[var(--color-gold-400)] hover:bg-[rgba(212,168,83,0.1)] flex items-center gap-1 disabled:opacity-50"
          >
            {expanding ? <Loader2 size={9} className="animate-spin" /> : <GitBranch size={9} />}
            Expand{anchor.expand_target_count ? ` ~${anchor.expand_target_count}` : ""}
          </button>
        )}
      </div>
    </div>
  );
}

export default function BreakdownPage({ projectId, script, onUpdate }) {
  const [anchors, setAnchors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [expandingId, setExpandingId] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const pendingPhotoreal = useMemo(
    () => anchors.filter((a) => a.status === "pending" && a.render_kind !== "screen_capture").length,
    [anchors]
  );

  const childrenByParent = useMemo(() => {
    const map = {};
    anchors.forEach((a) => {
      if (a.parent_anchor_id) (map[a.parent_anchor_id] ||= []).push(a);
    });
    return map;
  }, [anchors]);

  const sceneHeadings = useMemo(() => {
    const map = {};
    (script?.scenes || []).forEach((s) => { map[s.number] = s.heading; });
    return map;
  }, [script]);

  const load = useCallback(async () => {
    try {
      const data = await getImportedShots(projectId);
      setAnchors(data);
    } catch (err) {
      console.error("Failed to load imported shots", err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Top-level beats only (children render nested under their parent).
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    anchors.filter((a) => !a.parent_anchor_id).forEach((a) => {
      if (!cur || cur.scene !== a.scene_number) {
        cur = { scene: a.scene_number, heading: sceneHeadings[a.scene_number] || `Scene ${a.scene_number}`, items: [] };
        out.push(cur);
      }
      cur.items.push(a);
    });
    return out;
  }, [anchors, sceneHeadings]);

  const onCommitted = async () => {
    await load();
    onUpdate?.();
  };

  const handleExpand = async (anchor) => {
    setExpandingId(anchor.id);
    try {
      const data = await proposeExpansion({ project_id: projectId, parent_anchor_id: anchor.id });
      setReviewData(data);
    } catch (err) {
      toast.error("Could not propose expansion", { description: err.response?.data?.detail || err.message });
    } finally {
      setExpandingId(null);
    }
  };

  const handleRender = async () => {
    setRendering(true);
    try {
      const res = await visualizeImport({ project_id: projectId });
      toast.success(res.message || "Rendering started");
      setShowProgress(true);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Generation already in progress for this project");
      } else {
        toast.error("Could not start rendering", { description: err.response?.data?.detail || err.message });
      }
    } finally {
      setRendering(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-[var(--color-gold-400)]" /></div>;
  }

  return (
    <div className="h-full flex flex-col">
      {/* header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-[var(--border-subtle)]">
        <Clapperboard size={20} className="text-[var(--color-gold-400)]" />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-[var(--text-primary)]">Shot Breakdown</h1>
          <p className="text-xs text-[var(--text-muted)]">{anchors.length} imported shots · {groups.length} scenes</p>
        </div>
        {anchors.length > 0 && (
          <Button variant="ghost" onClick={() => window.__switchTab?.("storyboard")}>
            View in Storyboard <ArrowRight size={14} className="ml-1.5" />
          </Button>
        )}
        {pendingPhotoreal > 0 && (
          <Button variant="outline" onClick={handleRender} disabled={rendering}>
            {rendering ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
            Render {pendingPhotoreal} pending
          </Button>
        )}
        <Button className="btn-primary" onClick={() => setWizardOpen(true)}>
          <Plus size={14} className="mr-1.5" /> Import
        </Button>
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {anchors.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <Clapperboard size={40} className="text-[var(--text-muted)] mb-3" />
            <p className="text-sm font-medium text-[var(--text-primary)] mb-1">No shot breakdown imported yet</p>
            <p className="text-xs text-[var(--text-muted)] mb-4 max-w-sm">
              Upload a finished shot-breakdown document (a numbered shot list) and it becomes a divided,
              editable storyboard you can render and expand.
            </p>
            <Button className="btn-primary" onClick={() => setWizardOpen(true)}>
              <Plus size={14} className="mr-1.5" /> Import a breakdown
            </Button>
          </div>
        ) : (
          <div className="space-y-5 max-w-4xl">
            {groups.map((g) => (
              <div key={`sc-${g.scene}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MapPin size={11} className="text-[var(--text-muted)]" />
                  <span className="text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide">{g.heading}</span>
                  <span className="text-[10px] text-[var(--text-muted)]">· {g.items.length} shot{g.items.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-1.5">
                  {g.items.map((a) => (
                    <div key={a.id} className="space-y-1.5">
                      <ImportedShot anchor={a} onExpand={handleExpand} expanding={expandingId === a.id} />
                      {(childrenByParent[a.id] || []).map((c) => (
                        <ImportedShot key={c.id} anchor={c} child />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ShotBreakdownImportWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        projectId={projectId}
        onCommitted={onCommitted}
      />

      {showProgress && (
        <AutoStoryboardProgress
          projectId={projectId}
          onComplete={() => { setShowProgress(false); load(); onUpdate?.(); }}
          onDismiss={() => setShowProgress(false)}
        />
      )}

      <ExpansionReviewPanel
        open={!!reviewData}
        data={reviewData}
        projectId={projectId}
        onClose={() => setReviewData(null)}
        onCommitted={() => { load(); onUpdate?.(); }}
      />
    </div>
  );
}
